/**
 * Live password-requirements checklist.
 *
 * WHY THIS EXISTS
 * ---------------
 * The most any password field in this app said was "At least 8 characters", while
 * the identity service also required a letter, a number and a symbol — and, until
 * 2026-07-30, silently refused any character outside `A-Za-z0-9@$!%*#?&`, which
 * rejected hyphens, underscores, spaces, non-ASCII and therefore most
 * password-manager output and every passphrase.
 *
 * So a user could satisfy the only hint they were shown and still be rejected,
 * with nothing telling them what was missing. That is a stuck form, and on the
 * sign-up screen it is churn before the product is ever seen.
 *
 * A list that ticks as you type removes the guessing entirely. It is a HINT, not
 * a gate: submission is never blocked on it and the server remains the authority,
 * so this component being wrong or absent degrades to the previous behaviour
 * rather than to an unusable form.
 *
 * THE RULES MUST MATCH THE SERVER
 * -------------------------------
 * `validate_complex_password` in svdy-authorization/app/auth/schemas.py:
 *     ^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$
 *
 * `symbol` is deliberately "anything that is not a letter or a digit" rather than
 * a fixed list — that is what the server now accepts, and stating a list here
 * would re-create the very restriction that was just removed.
 *
 * There is no way to share this code with the other three repositories, so
 * `passwordRules.test.ts` pins the rule set instead.
 *
 * THE VISUAL CONTRACT — identical in all four repositories
 * -------------------------------------------------------
 * The first version of this block was rejected on sight, and the reasons are
 * worth keeping because each one is a rule, not a taste:
 *
 * 1. ONE COLUMN, one rule per row. It was a `flexWrap` row, so the four rules
 *    packed themselves 3+1 in a dialog, 4+0 on a wider page and 3+1 again in the
 *    hosted template — the same list with a different silhouette on every
 *    surface, and a ragged right edge on each (svdy-ui §11.4). A single column
 *    cannot wrap, so it is identical everywhere, at any width, in RTL too.
 * 2. A DOT, not a hollow circle. `RadioButtonUnchecked` is the glyph for an
 *    unselected radio button, so four of them under a field read as a control
 *    the user is supposed to operate rather than as status.
 * 3. THE GLYPH CARRIES THE STATE, not the text colour. Turning each met row
 *    green made a satisfied password a block of four green lines shouting at
 *    someone who has already done what was asked.
 * 4. NO THIRD LINE. There were three stacked grey lines in three different
 *    greys (title / rules / "Spaces and any other characters are fine."). The
 *    relaxation is now stated inside the rule it belongs to — "a symbol or
 *    space" — which is both shorter and more concrete.
 * 5. NO OPACITY DIM. Fading the untouched block to 0.75 added a fourth grey and
 *    made the text muddy for the state the user sees FIRST.
 */
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface PasswordRule {
  key: 'length' | 'letter' | 'number' | 'symbol';
  test: (value: string) => boolean;
}

/** The four requirements, in the order they are shown. */
export const PASSWORD_RULES: PasswordRule[] = [
  { key: 'length', test: (v) => v.length >= 8 },
  { key: 'letter', test: (v) => /[A-Za-z]/.test(v) },
  { key: 'number', test: (v) => /\d/.test(v) },
  { key: 'symbol', test: (v) => /[^A-Za-z\d]/.test(v) },
];

/** True when a value satisfies every requirement. */
export function passwordMeetsRules(value: string): boolean {
  return PASSWORD_RULES.every((r) => r.test(value));
}

export const PasswordRequirements: React.FC<{ value: string }> = ({ value }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5 }}
      >
        {t('common:passwordRules.title')}
      </Typography>
      <Stack spacing={0.25}>
        {PASSWORD_RULES.map((rule) => {
          const met = rule.test(value);
          return (
            // `useFlexGap` rather than Stack's default margin: `gap` is
            // direction-agnostic, so the space between mark and label survives
            // in Arabic without depending on the RTL stylis plugin being wired.
            <Stack key={rule.key} direction="row" alignItems="center" spacing={0.75} useFlexGap>
              {/* Fixed-width slot: the row cannot shift sideways as it goes from
                  unmet to met, so the text stays on one alignment axis. */}
              <Box
                sx={{
                  width: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {met ? (
                  <CheckRounded sx={{ fontSize: 14, color: 'success.main' }} />
                ) : (
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      bgcolor: 'text.disabled',
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t(`common:passwordRules.${rule.key}`)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};
export default PasswordRequirements;
